import React from 'react';
import type {
  AutoGateStatus,
  DeliveryStatus,
  ManualReviewStatus,
  ScanStatus,
  WorkflowStatus,
} from '../../lib/adminReviewApi';

type BadgeTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger' | 'purple';

const TONE_CLASSES: Record<BadgeTone, string> = {
  neutral:
    'bg-secondary-100 text-secondary-700 dark:bg-secondary-700 dark:text-secondary-200',
  info: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
  success: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
  warning: 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200',
  danger: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
  purple: 'bg-primary-100 text-primary-800 dark:bg-primary-900/40 dark:text-primary-200',
};

function workflowTone(status?: WorkflowStatus): BadgeTone {
  switch (status) {
    case 'needs_review':
    case 'queued':
      return 'warning';
    case 'in_review':
      return 'info';
    case 'approved':
    case 'approved_with_note':
      return 'success';
    case 'held':
    case 'needs_retry':
    case 'retry_in_progress':
      return 'danger';
    case 'delivered':
      return 'purple';
    case 'feedback_requested':
    case 'feedback_received':
      return 'info';
    default:
      return 'neutral';
  }
}

function deliveryTone(status?: DeliveryStatus): BadgeTone {
  switch (status) {
    case 'ready_for_review':
      return 'warning';
    case 'approved_internal':
      return 'success';
    case 'delivered':
      return 'purple';
    case 'held':
    case 'failed':
    case 'retry_required':
      return 'danger';
    default:
      return 'neutral';
  }
}

function autoGateTone(status?: AutoGateStatus): BadgeTone {
  switch (status) {
    case 'auto_approved':
      return 'success';
    case 'needs_review':
      return 'warning';
    case 'held':
    case 'retry_required':
    case 'failed':
      return 'danger';
    default:
      return 'neutral';
  }
}

function scanTone(status?: ScanStatus): BadgeTone {
  switch (status) {
    case 'pass':
      return 'success';
    case 'fail':
      return 'danger';
    default:
      return 'neutral';
  }
}

interface StatusBadgeProps {
  label: string;
  tone?: BadgeTone;
  title?: string;
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({ label, tone = 'neutral', title }) => (
  <span
    title={title}
    className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${TONE_CLASSES[tone]}`}
  >
    {label}
  </span>
);

export const WorkflowStatusBadge: React.FC<{ status?: WorkflowStatus }> = ({ status }) => (
  <StatusBadge label={status || 'unknown'} tone={workflowTone(status)} />
);

export const DeliveryStatusBadge: React.FC<{ status?: DeliveryStatus }> = ({ status }) => (
  <StatusBadge label={status || 'unknown'} tone={deliveryTone(status)} />
);

export const AutoGateStatusBadge: React.FC<{ status?: AutoGateStatus }> = ({ status }) => (
  <StatusBadge label={status || 'unknown'} tone={autoGateTone(status)} />
);

export const ManualReviewStatusBadge: React.FC<{ status?: ManualReviewStatus }> = ({ status }) => (
  <StatusBadge label={status || 'unknown'} tone={status === 'pass' || status === 'pass_with_note' ? 'success' : status === 'fail' ? 'danger' : 'neutral'} />
);

export const ScanStatusBadge: React.FC<{ status?: ScanStatus; count?: number }> = ({
  status,
  count,
}) => (
  <StatusBadge
    label={count !== undefined && status !== 'not_run' ? `${status} (${count})` : status || 'not_run'}
    tone={scanTone(status)}
  />
);
